import { trustHTML } from '@ember/template';
import Component from '@glimmer/component';
import { eq } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';

import type { BarData } from '../../../types/components/prism/data-bar.ts';
import {
  COLOR_TRAFFIC_LIGHT_NEUTRAL,
  getCssVariableValue,
} from '../../../utils/css-variable.ts';
import styles from './data-bar-basic.module.css';

type BarDirection = 'regular' | 'reverse';

type ProgressValue = {
  isGradient: boolean;
  isStriped: boolean;
  style: string;
};

interface PrismDataBarDataBarBasicSignature {
  Args: {
    backgroundColor?: string;
    data?: BarData[];
    dataBarThresholdStyle?: string;
    direction?: BarDirection;
    isGradient?: boolean;
    isStriped?: boolean;
    rounded?: 'left' | 'right' | 'both';
  };
  Element: HTMLDivElement;
}

export default class PrismDataBarDataBarBasic extends Component<PrismDataBarDataBarBasicSignature> {
  get direction(): BarDirection {
    return this.args.direction ?? 'regular';
  }

  get progressValues(): ProgressValue[] {
    const { data } = this.args;

    if (!data) {
      return [
        {
          isGradient: false,
          isStriped: false,
          style: [
            `background-color: ${getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL)};`,
            'width: 0%;',
          ].join(' '),
        },
      ];
    }

    return data
      .filter((datum) => {
        // Do not render progress bars with 0 width, if it has a border it will be visible
        return (datum?.widthPercentage ?? 0) !== 0;
      })
      .map((datum) => {
        const backgroundColor =
          datum?.backgroundColor ??
          getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL);
        const widthPercentage = datum?.widthPercentage ?? 0;

        const isGradient = Boolean(datum.isGradient || this.args.isGradient);
        const isStriped = Boolean(datum.isStriped || this.args.isStriped);

        if (isGradient || isStriped) {
          return {
            isGradient,
            isStriped,
            style: `width: ${widthPercentage}%`,
          };
        }

        return {
          isGradient: false,
          isStriped: false,
          style: [
            `background-color: ${backgroundColor};`,
            `width: ${widthPercentage}%;`,
          ].join(' '),
        };
      });
  }

  get styleForBackgroundColor(): string {
    const { backgroundColor } = this.args;

    return backgroundColor ? `background-color: ${backgroundColor};` : '';
  }

  <template>
    <div
      class={{local
        styles
        "container"
        (if (eq @rounded "left") "round-left")
        (if (eq @rounded "right") "round-right")
      }}
      style={{trustHTML this.styleForBackgroundColor}}
      ...attributes
    >
      <div
        class={{styles.inner-container}}
        style={{if @dataBarThresholdStyle (trustHTML @dataBarThresholdStyle)}}
      >
        {{#each this.progressValues as |progressValue|}}
          <div
            class={{local
              styles
              "bar"
              this.direction
              (if progressValue.isStriped "show-stripe")
              (if progressValue.isGradient "show-gradient")
            }}
            data-test-point
            role="progressbar"
            style={{progressValue.style}}
          ></div>
        {{/each}}
      </div>
    </div>
  </template>
}
